import * as z from "zod/mini";

import {all_messages_data} from "./all_messages_data.ts";
import * as breakpage_view from "./breakpage_view.ts";
import * as browser_history from "./browser_history.ts";
import * as channel from "./channel.ts";
import {FetchStatus} from "./fetch_status.ts";
import type {Filter} from "./filter.ts";
import * as inbox_ui from "./inbox_ui.ts";
import * as left_sidebar_custom from "./left_sidebar_custom.ts";
import * as lightbox from "./lightbox.ts";
import * as message_edit_history from "./message_edit_history.ts";
import * as message_fetch from "./message_fetch.ts";
import {compare_message_ids, get_min_message_id, message_id_schema} from "./message_id.ts";
import type {MessageId} from "./message_id.ts";
import * as message_list_data_cache from "./message_list_data_cache.ts";
import * as message_lists from "./message_lists.ts";
import * as message_store from "./message_store.ts";
import * as message_view from "./message_view.ts";
import * as message_view_header from "./message_view_header.ts";
import * as narrow_state from "./narrow_state.ts";
import * as recent_senders from "./recent_senders.ts";
import * as recent_view_data from "./recent_view_data.ts";
import * as recent_view_ui from "./recent_view_ui.ts";
import {topic_settings_event_schema} from "./server_event_types.ts";
import * as starred_messages from "./starred_messages.ts";
import * as starred_messages_ui from "./starred_messages_ui.ts";
import * as stream_data from "./stream_data.ts";
import * as stream_list from "./stream_list.ts";
import * as stream_topic_history from "./stream_topic_history.ts";
import * as stream_topic_history_util from "./stream_topic_history_util.ts";
import {api_stream_schema} from "./stream_types.ts";
import * as sub_store from "./sub_store.ts";
import * as topic_appearance from "./topic_appearance.ts";
import * as topic_description from "./topic_description.ts";
import * as topic_list from "./topic_list.ts";
import * as topic_permissions from "./topic_permissions.ts";
import * as topic_settings from "./topic_settings.ts";
import * as unread from "./unread.ts";
import * as unread_ui from "./unread_ui.ts";

export const snapshot_schema = z.object({
    topic_access: z.array(api_stream_schema),
    topic_appearances: z.array(topic_appearance.topic_appearance_schema),
    starred_messages: z.array(message_id_schema),
    unread_messages: z.array(z.object({
        id: message_id_schema,
        stream_id: z.number(),
        topic: z.string(),
        mentioned: z.boolean(),
    })),
});

function filter_may_include_streams(filter: Filter, stream_ids: Set<number>): boolean {
    if (filter.has_operator("dm") || filter.has_operand("is", "dm")) {
        return false;
    }
    const channels = filter.operands("channel");
    return channels.length === 0 || channels.some((id) => stream_ids.has(Number(id)));
}

function clear_unreads(stream_ids: Set<number>): void {
    for (const stream_id of stream_ids) {
        for (const id of unread.get_msg_ids_for_stream(stream_id)) {
            // Only change the local index. Never mark revoked messages read
            // on the server; their receipts and flags must survive a regrant.
            unread.mark_as_read(id);
        }
    }
}

function invalidate_shared_coverage(ids: MessageId[]): void {
    const previous_oldest = all_messages_data.first_including_muted()?.id;
    recovery_oldest_id = get_min_message_id([
        ...ids,
        ...(previous_oldest === undefined ? [] : [previous_oldest]),
        ...(recovery_oldest_id === undefined ? [] : [recovery_oldest_id]),
    ]);
    all_messages_data.access_generation += 1;
    all_messages_data.clear();
    all_messages_data.fetch_status = new FetchStatus();
}

function invalidate_messages(stream_ids: Set<number>): boolean {
    const known_streams = new Set([...stream_ids].filter((id) => {
        const sub = sub_store.get(id);
        return sub?.subscribed || (sub?.topic_access?.length ?? 0) > 0 ||
            message_lists.current?.data.filter.operands("channel").includes(String(id));
    }));
    const ids = [...stream_ids].flatMap((id) => message_store.get_message_ids_in_stream(id))
        .filter((id) => !message_store.get(id)?.locally_echoed);
    if (known_streams.size === 0 && ids.length === 0) {
        return false;
    }
    const id_set = new Set(ids);
    breakpage_view.invalidate_streams(stream_ids, id_set);
    left_sidebar_custom.invalidate_messages(id_set);
    lightbox.invalidate_messages(id_set);
    message_edit_history.invalidate_messages(id_set);
    recent_senders.update_topics_of_deleted_message_ids(ids);
    for (const list of message_lists.all_rendered_message_lists()) {
        if (!filter_may_include_streams(list.data.filter, stream_ids)) {
            continue;
        }
        list.data.access_generation += 1;
        list.data.remove(ids);
        list.rerender();
        if (list !== message_lists.current) {
            message_lists.delete_message_list(list);
        }
    }
    for (const data of message_list_data_cache.all()) {
        if (data !== all_messages_data && filter_may_include_streams(data.filter, stream_ids)) {
            data.access_generation += 1;
            data.remove(ids);
            message_list_data_cache.remove(data.filter);
        }
    }
    // This cache promises a continuous range. After either a grant or a
    // revocation it cannot populate another narrow without a fetch.
    // This does not clear unaffected rendered lists or recent conversations.
    const recent_ids: MessageId[] = [];
    for (const [key, conversation] of recent_view_data.conversations) {
        if (conversation.type === "stream" && stream_ids.has(Number(key.split(":")[0]))) {
            if (conversation.last_msg_id !== -1) {
                recent_ids.push(conversation.last_msg_id);
            }
            recent_view_data.conversations.delete(key);
        }
    }
    invalidate_shared_coverage([...ids, ...recent_ids]);
    message_store.remove(ids);
    starred_messages.remove(ids);
    clear_unreads(stream_ids);
    for (const id of stream_ids) {
        stream_topic_history.remove_history_for_stream(id);
    }
    recent_view_ui.complete_rerender();
    inbox_ui.complete_rerender();
    unread_ui.update_unread_counts();
    stream_list.update_streams_sidebar(true);
    topic_list.update();
    message_view_header.render_title_area();
    return true;
}

function rerender_metadata(stream_ids: Set<number>): void {
    for (const list of message_lists.all_rendered_message_lists()) {
        if (list.data.all_messages().some((message) =>
            message.type === "stream" && stream_ids.has(message.stream_id))) {
            list.rerender();
        }
    }
    topic_list.update();
    stream_list.update_streams_sidebar(true);
    recent_view_ui.complete_rerender();
    inbox_ui.complete_rerender();
    message_view_header.render_title_area();
}

// Coalesce overlapping notifications. No source topic names, policies or HTML
// arrive in an event; all display data comes from a viewer-filtered read.
const pending_streams = new Set<number>();
const pending_access_streams = new Set<number>();
const changed_unread_ids = new Set<MessageId>();
const changed_starred_ids = new Set<MessageId>();
const moved_messages = new Map<MessageId, {stream_id?: number; topic?: string}>();
let recovery_oldest_id: MessageId | undefined;
let snapshot_request: ReturnType<typeof channel.get>;
let pending_messages = false;
let saved_position: {
    list_id: number;
    message_id: ReturnType<NonNullable<typeof message_lists.current>["selected_id"]>;
    offset: number | undefined;
} | undefined;
let generation = 0;
let retry_timer: ReturnType<typeof setTimeout> | undefined;

const intervening_event_schema = z.object({
    type: z.string(),
    message: z.optional(z.object({id: message_id_schema})),
    messages: z.optional(z.array(message_id_schema)),
    message_ids: z.optional(z.array(message_id_schema)),
    flag: z.optional(z.string()),
    stream_id: z.optional(z.number()),
    new_stream_id: z.optional(z.number()),
    subject: z.optional(z.string()),
});

// Ordinary events continue to be applied normally. Remember only the IDs whose
// live state must win over an overlapping snapshot, rather than retrying until
// the entire organization stops sending messages.
export function record_event(raw_event: unknown): void {
    if (pending_streams.size === 0) {
        return;
    }
    const parsed = intervening_event_schema.safeParse(raw_event);
    if (!parsed.success) {
        return;
    }
    const event = parsed.data;
    if (event.type === "message" && event.message) {
        changed_unread_ids.add(event.message.id);
        changed_starred_ids.add(event.message.id);
    } else if (event.type === "delete_message") {
        for (const id of event.message_ids ?? []) {
            changed_unread_ids.add(id);
            changed_starred_ids.add(id);
        }
    } else if (event.type === "update_message_flags") {
        const changed = event.flag === "read" ? changed_unread_ids :
            event.flag === "starred" ? changed_starred_ids : undefined;
        for (const id of event.messages ?? []) {
            changed?.add(id);
        }
    } else if (event.type === "update_message" &&
        (event.new_stream_id !== undefined || event.subject !== undefined)) {
        for (const id of event.message_ids ?? []) {
            const previous = moved_messages.get(id);
            moved_messages.set(id, {
                stream_id: event.new_stream_id ?? event.stream_id ?? previous?.stream_id,
                topic: event.subject ?? previous?.topic,
            });
        }
    }
}

function recover_message_coverage(): void {
    const access_generation = all_messages_data.access_generation;
    const oldest = recovery_oldest_id;
    function fetch(anchor: MessageId | "newest"): void {
        message_fetch.load_messages({
            msg_list_data: all_messages_data, anchor, num_before: 1000, num_after: 0,
            cont(data) {
                if (access_generation !== all_messages_data.access_generation) {
                    return;
                }
                const first_id = get_min_message_id(data.messages.map((message) => message.id));
                if (oldest !== undefined && first_id !== undefined && !data.found_oldest &&
                    compare_message_ids(first_id, oldest) > 0 &&
                    (anchor === "newest" || compare_message_ids(first_id, anchor) < 0)) {
                    fetch(first_id);
                } else {
                    recovery_oldest_id = undefined;
                }
            },
        });
    }
    fetch("newest");
}

export function handle_event(raw_event: unknown): void {
    const event = topic_settings_event_schema.parse(raw_event);
    const stream_ids = new Set(event.stream_ids);
    channel.invalidate_topic_requests();
    topic_permissions.invalidate_streams(stream_ids);
    topic_description.invalidate_streams(stream_ids);
    topic_settings.revalidate_streams(stream_ids);
    if (event.invalidate_messages) {
        for (const id of stream_ids) {
            pending_access_streams.add(id);
        }
        if (message_lists.current !== undefined && saved_position?.list_id !== message_lists.current.id) {
            saved_position = {
                list_id: message_lists.current.id,
                message_id: message_lists.current.selected_id(),
                offset: browser_history.current_scroll_offset(),
            };
        }
        topic_appearance.remove_for_streams(stream_ids);
        pending_messages = invalidate_messages(stream_ids) || pending_messages;
    }
    for (const id of stream_ids) {
        pending_streams.add(id);
    }
    generation += 1;
    refresh(generation);
}

function refresh(request_generation: number, attempt = 0): void {
    snapshot_request?.abort();
    snapshot_request = undefined;
    changed_unread_ids.clear();
    changed_starred_ids.clear();
    moved_messages.clear();
    if (retry_timer !== undefined) {
        clearTimeout(retry_timer);
        retry_timer = undefined;
    }
    const stream_ids = new Set(pending_streams);
    const retry = (): void => {
        if (request_generation !== generation) {
            return;
        }
        // Leave revoked content cleared on failure, including a malformed
        // response. Never reload or discard the user's draft.
        retry_timer = setTimeout(
            () => refresh(request_generation, attempt + 1),
            Math.min(30_000, 1000 * 2 ** Math.min(attempt, 5)),
        );
    };
    snapshot_request = channel.get({
        url: "/json/topic_settings",
        data: {stream_ids: JSON.stringify([...stream_ids])},
        success(raw_data) {
            if (request_generation !== generation) {
                return;
            }
            snapshot_request = undefined;
            const parsed = snapshot_schema.safeParse(raw_data);
            if (!parsed.success) {
                retry();
                return;
            }
            const data = parsed.data;
            const affects_display = [...stream_ids].some((id) => {
                const sub = sub_store.get(id);
                return sub?.subscribed || (sub?.topic_access?.length ?? 0) > 0 ||
                    message_store.get_message_ids_in_stream(id).length > 0 ||
                    message_lists.current?.data.filter.operands("channel").includes(String(id)) ||
                    data.topic_access.some((stream) => stream.stream_id === id);
            });
            const gained = new Set(data.topic_access.filter((stream) => {
                if (!pending_access_streams.has(stream.stream_id)) {
                    // Creating an empty topic can add navigation, but cannot
                    // change access to any existing channel messages.
                    return false;
                }
                const previous = sub_store.get(stream.stream_id)?.topic_access ?? [];
                const previous_names = new Set(previous.map((entry) => entry.topic.toLowerCase()));
                return stream.topic_access?.some((entry) => !previous_names.has(entry.topic.toLowerCase()));
            }).map((stream) => stream.stream_id));
            const previous_current = message_lists.current;
            const position = saved_position?.list_id === previous_current?.id ? saved_position : undefined;
            const selected_id = position?.message_id ?? previous_current?.selected_id();
            const access_by_id = new Map(data.topic_access.map((stream) => [stream.stream_id, stream]));
            const navigation_changed = new Set<number>();
            for (const id of stream_ids) {
                const sub = sub_store.get(id);
                const previous_names = (sub?.topic_access ?? []).map((entry) => entry.topic).sort();
                const next_names = (access_by_id.get(id)?.topic_access ?? []).map((entry) => entry.topic).sort();
                if (JSON.stringify(previous_names) !== JSON.stringify(next_names)) {
                    navigation_changed.add(id);
                    stream_topic_history.remove_history_for_stream(id);
                }
                if (sub === undefined) {
                    continue;
                }
                sub.topic_access = access_by_id.get(id)?.topic_access ?? [];
                if (sub.topic_only && !sub.subscribed && !access_by_id.has(id)) {
                    stream_list.remove_sidebar_row(id);
                    stream_data.delete_sub(id);
                }
            }
            stream_data.create_streams(data.topic_access);
            // Newly gained topic-only channels were unknown when the event
            // arrived. Invalidate coverage before using their navigation data.
            if (gained.size > 0 && !pending_messages) {
                // Newly delivered live events already have current access.
                // Do not purge them a second time when navigation arrives.
                invalidate_shared_coverage([]);
                pending_messages = true;
            }
            topic_appearance.remove_for_streams(stream_ids);
            for (const appearance of data.topic_appearances) {
                topic_appearance.set(appearance.stream_id, appearance.topic_name, appearance);
            }
            topic_description.invalidate_streams(stream_ids);
            topic_permissions.invalidate_streams(stream_ids);
            if (pending_messages) {
                for (const message of data.unread_messages) {
                    if (!pending_access_streams.has(message.stream_id) || changed_unread_ids.has(message.id)) {
                        continue;
                    }
                    const moved = moved_messages.get(message.id);
                    unread.process_unread_message({
                        ...message, type: "stream", unread: true, mentioned_me_directly: undefined,
                        stream_id: moved?.stream_id ?? message.stream_id,
                        topic: moved?.topic ?? message.topic,
                    });
                }
                const current_stars = starred_messages.get_starred_msg_ids();
                starred_messages.initialize({starred_messages: [
                    ...data.starred_messages.filter((id) => !changed_starred_ids.has(id)),
                    ...current_stars.filter((id) => changed_starred_ids.has(id)),
                ]});
                starred_messages_ui.rerender_ui();
                unread_ui.update_unread_counts();
                recover_message_coverage();
                if (previous_current !== undefined && narrow_state.is_message_feed_visible() &&
                    filter_may_include_streams(previous_current.data.filter, pending_access_streams)) {
                    message_view.show(previous_current.data.filter.terms(), {
                        then_select_id: selected_id, force_rerender: true,
                        then_select_offset: position?.offset,
                        trigger: "topic settings changed",
                    });
                }
            }
            for (const id of stream_ids) {
                const sub = sub_store.get(id);
                if (sub && !sub.is_archived && (sub.subscribed || (sub.topic_access?.length ?? 0) > 0)) {
                    stream_list.create_sidebar_row(sub, true);
                    if (navigation_changed.has(id) || (pending_messages && pending_access_streams.has(id))) {
                        stream_topic_history_util.get_server_history(id, () => topic_list.update());
                    }
                }
            }
            pending_streams.clear();
            pending_access_streams.clear();
            pending_messages = false;
            saved_position = undefined;
            if (affects_display) {
                rerender_metadata(stream_ids);
            }
        },
        error(_xhr, status) {
            if (request_generation === generation) {
                snapshot_request = undefined;
                if (status !== "abort") {
                    retry();
                }
            }
        },
    });
}
